/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchEmspSessionFilters,
  fetchEmspSessions,
  setSortData,
  updateEMSPSessionsFilters,
} from '../../../../redux/slices/OCPI/EMSPSessionsSlice';
import { RootState } from '../../../../redux/store';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiSession {
  id: string;
  status?: string;
  kwh?: number;
  currency?: string;
  total_cost?: { excl_vat?: number; incl_vat?: number };
  start_date_time?: string;
  end_date_time?: string;
  location_id?: string;
  evse_uid?: string;
  connector_id?: string;
  auth_method?: string;
  tariff_id?: string;
  last_updated?: string;
  _objectId?: string;
  _lastUpdated?: string;
  cpoCountryCode?: string;
  cpoPartyId?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * Renders a status badge for a session.
 * @param {any} props The props.
 * @param {any} props.status The status.
 */
function StatusBadge({ status }: Readonly<{ status: string }>) {
  let modifier = 'cpo-session-status-badge--pending';
  if (status === 'ACTIVE') {
    modifier = 'cpo-session-status-badge--active';
  } else if (status === 'COMPLETED') {
    modifier = 'cpo-session-status-badge--completed';
  } else if (status === 'INVALID') {
    modifier = 'cpo-session-status-badge--invalid';
  }

  return <span className={`cpo-session-status-badge ${modifier}`}>{status}</span>;
}

// ── Session Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders read-only details of a session received from a connected CPO.
 * @param {any} props The props.
 * @param {any} props.session The session details.
 * @param {any} props.onClose The close callback.
 */
function SessionDetailsModal({
  session,
  onClose,
}: Readonly<{
  session: OcpiSession;
  onClose: () => void;
}>) {
  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-session-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{session.id}</h3>
              <p className="cpo-drilldown-subtitle">
                {session.status ? <StatusBadge status={session.status} /> : '–'}
                {session.cpoCountryCode && session.cpoPartyId
                  ? ` · Source: ${session.cpoCountryCode} / ${session.cpoPartyId}`
                  : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <CloseIcon width={30} height={30} />
            </button>
          </div>

          {/* Raw session detail */}
          <div className="cpo-drilldown-body">
            <pre className="cpo-session-drilldown-detail">{JSON.stringify(session, null, 2)}</pre>
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

interface SessionRow {
  id: string;
  status: string;
  kwh: string;
  totalCost: string;
  cpo: string;
  locationId: string;
  evseUid: string;
  connectorId: string;
  authMethod: string;
  tariffId: string;
  startDatetime: string;
  endDatetime: string;
  lastUpdated: string;
  _createdAt?: string;
  createdAt: string;
  raw: OcpiSession;
}

/**
 * EmspSessionsPanel component displays OCPI charging sessions received from connected CPOs.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 * @param {any} props.filterCountryCode Optional country code of the CPO to filter by.
 * @param {any} props.filterPartyId Optional party ID of the CPO to filter by.
 * @param {any} props.refreshTrigger Optional number that triggers a reload when incremented.
 */
export default function EmspSessionsPanel({
  countryCode,
  partyId,
  filterCountryCode,
  filterPartyId,
  refreshTrigger,
}: Readonly<{
  countryCode: string;
  partyId: string;
  filterCountryCode?: string;
  filterPartyId?: string;
  refreshTrigger?: number;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.emspSessions);
  const [selectedSession, setSelectedSession] = useState<OcpiSession | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches eMSP sessions for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(
        fetchEmspSessions({
          countryCode,
          partyId,
          counterPartyId: filterPartyId,
          counterCountryCode: filterCountryCode,
          ...data,
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(
      fetchEmspSessions({
        countryCode,
        partyId,
        counterPartyId: filterPartyId,
        counterCountryCode: filterCountryCode,
      }),
    );
  }, [countryCode, partyId, filterPartyId, filterCountryCode, dispatch, refreshTrigger]);

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof SessionRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {SessionRow} currentRow The current session row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: SessionRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:sessionId'),
      accessorKey: 'id' as keyof SessionRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the session ID in a code badge.
       * @param {any} row The session row.
       */
      customData: (row: SessionRow) => <code className="cpo-session-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:sourceCpo'),
      accessorKey: 'cpo' as keyof SessionRow,
      sortAllow: false,
      /**
       * Renders the source CPO badge.
       * @param {any} row The session row.
       */
      customData: (row: SessionRow) => <span className="cpo-source-cpo-badge">{row.cpo}</span>,
    },
    {
      label: t('ocpi:status'),
      accessorKey: 'status' as keyof SessionRow,
      sortAllow: false,
      /**
       * Renders the status badge.
       * @param {any} row The session row.
       */
      customData: (row: SessionRow) => <StatusBadge status={row.status} />,
    },
    {
      label: t('ocpi:energy'),
      accessorKey: 'kwh' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:totalCost'),
      accessorKey: 'totalCost' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:locationId'),
      accessorKey: 'locationId' as keyof SessionRow,
      sortAllow: false,
      /**
       * Renders the location ID in a code badge.
       * @param {any} row The session row.
       */
      customData: (row: SessionRow) => (
        <code className="cpo-session-id-badge">{String(row.locationId).slice(0, 18)}</code>
      ),
    },
    {
      label: t('ocpi:evseUid'),
      accessorKey: 'evseUid' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:connectorId'),
      accessorKey: 'connectorId' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:authMethod'),
      accessorKey: 'authMethod' as keyof SessionRow,
      sortAllow: false,
    },
    // {
    //   label: t('ocpi:tariffId'),
    //   accessorKey: 'tariffId' as keyof SessionRow,
    //   sortAllow: false,
    //   /**
    //    * Renders the tariff ID in a code badge.
    //    * @param {any} row The session row.
    //    */
    //   customData: (row: SessionRow) => (
    //     <code className="cpo-session-id-badge">{String(row.tariffId).slice(0, 18)}</code>
    //   ),
    // },
    {
      label: t('ocpi:started'),
      accessorKey: 'startDatetime' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:ended'),
      accessorKey: 'endDatetime' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof SessionRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated'),
      accessorKey: 'lastUpdated' as keyof SessionRow,
      sortAllow: false,
    },
  ];

  const tableData: SessionRow[] = sessions.map((session: OcpiSession) => {
    const sessionId = session.id || session._objectId || '';
    const dateString = session.last_updated || session._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const startDatetime = session.start_date_time
      ? new Date(session.start_date_time).toLocaleString()
      : '–';
    const endDatetime = session.end_date_time
      ? new Date(session.end_date_time).toLocaleString()
      : '–';
    const cost = session.total_cost?.incl_vat ?? session.total_cost?.excl_vat;
    const totalCost = cost !== undefined ? `${cost} ${session.currency || ''}`.trim() : '–';
    const sourceCpo =
      session.cpoCountryCode && session.cpoPartyId
        ? `${session.cpoCountryCode} / ${session.cpoPartyId}`
        : '–';
    const createdAt =
      session._createdAt ||
      session.createdAt ||
      (session as any).created_at ||
      (session as any).created ||
      '';

    return {
      id: sessionId,
      status: session.status || t('ocpi:unknown'),
      kwh: session.kwh !== undefined ? `${session.kwh} kWh` : '–',
      totalCost,
      cpo: sourceCpo,
      locationId: session.location_id || '–',
      evseUid: session.evse_uid || '–',
      connectorId: session.connector_id || '–',
      authMethod: session.auth_method || '–',
      tariffId: session.tariff_id || '–',
      startDatetime,
      endDatetime,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: session,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField, sortBy });
  };

  /**
   * Handle the table's sort change.
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  return (
    <div className="cpo-sessions-panel">
      {/* ── Top bar ── */}
      <div className="cpo-sessions-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:receivedCpoSessions')}</h3>
          <p className="cpo-panel-subtitle">
            {sessions.length > 0
              ? `${sessions.length} ${t(
                  sessions.length !== 1 ? 'ocpi:sessionCountPlural' : 'ocpi:sessionCountSingular',
                )} received from connected CPOs`
              : t('ocpi:noSessionsReceived')}
          </p>
        </div>
      </div>

      {/* ── Sessions Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateEMSPSessionsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchEmspSessionFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedSession(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
      />

      {/* ── Drill-down modal ── */}
      {selectedSession && (
        <SessionDetailsModal session={selectedSession} onClose={() => setSelectedSession(null)} />
      )}
    </div>
  );
}

EmspSessionsPanel.defaultProps = {
  filterCountryCode: undefined,
  filterPartyId: undefined,
  refreshTrigger: undefined,
};
